// src/hooks/useCanvasAutosave.js
// ---------------------------------------------------------------------------
// Hook: efficient autosave for the unified wraparound Fabric.js canvas.
//
// Strategy:
//   1. Every canvas change  -> dirty = true, debounced local save (400 ms).
//   2. Every 60 s           -> if dirty, sync to server (one PUT).
//   3. Manual save / export -> immediate server flush.
//   4. Tab close            -> best-effort sendBeacon flush.
// ---------------------------------------------------------------------------
import { useEffect, useRef, useState, useCallback } from 'react';
import { API_BASE } from '../utils/api';
import { setLocalDesign, clearLocalDesign } from '../utils/canvasStorage';
import { useToast } from '../context/ToastContext';

// -- Tuning constants ---------------------------------------------------------
const LOCAL_DEBOUNCE_MS = 400;   // debounce for localStorage writes
const SERVER_SYNC_MS   = 60000;  // periodic server sync interval (60 s)

/**
 * @param {fabric.Canvas|null} canvas
 * @param {string}  draftId
 * @param {string|null}  versionId
 * @param {boolean} readOnly
 * @param {boolean} isHydrating - true while initial canvas load is in progress;
 *                                 listeners are suppressed until this is false
 * @returns {{
 *   saveStatus: 'saved'|'saving'|'unsaved',
 *   triggerChange: () => void,
 *   forceSave: (silent?: boolean) => Promise<void>,
 * }}
 */
export default function useCanvasAutosave(canvas, draftId, versionId, readOnly, isHydrating, faceDims, hasCoverImage = true) {
  const [saveStatus, setSaveStatus] = useState('saved');
  const toast = useToast();

  // Refs survive re-renders without causing effect re-runs
  const dirtyRef           = useRef(false);
  const localTimerRef      = useRef(null);
  const serverTimerRef     = useRef(null);
  const savingRef          = useRef(false); // guards concurrent saves
  const hasCoverImageRef   = useRef(hasCoverImage);
  const didUploadThumbRef  = useRef(false);
  const silentFailNotedRef = useRef(false); // one warning per silent-failure streak

  // Keep latest values in refs so callbacks never go stale
  const canvasRef    = useRef(canvas);
  const draftIdRef   = useRef(draftId);
  const versionIdRef = useRef(versionId);
  const faceDimsRef  = useRef(faceDims);
  useEffect(() => { canvasRef.current    = canvas;    }, [canvas]);
  useEffect(() => { draftIdRef.current   = draftId;   }, [draftId]);
  useEffect(() => { versionIdRef.current = versionId; }, [versionId]);
  useEffect(() => { faceDimsRef.current  = faceDims;  }, [faceDims]);
  useEffect(() => { hasCoverImageRef.current = hasCoverImage; }, [hasCoverImage]);

  // Reset per-session thumbnail flag when version changes
  useEffect(() => { didUploadThumbRef.current = false; }, [versionId]);

  // -- Helpers ----------------------------------------------------------------

  /** Snapshot the canvas design JSON with background color preserved. */
  const getDesignSnapshot = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return null;
    const design = c.toJSON(['name', '_collabId', '_locked']);
    design.backgroundColor = c.backgroundColor || design.backgroundColor || '#ffffff';
    // Persist top-level flags that Fabric doesn't serialize
    if (c._hasIsbn !== undefined) design.hasIsbn = !!c._hasIsbn;
    if (c._coverMargins) design.coverMargins = c._coverMargins;
    if (c._thicknessMode) design.thicknessMode = c._thicknessMode;
    // Store face dimensions so the loader knows what coords objects were saved at
    if (faceDimsRef.current) design.faceDims = { ...faceDimsRef.current };
    // Backup per-face background colors. Bg-rect `name` restoration can fail
    // on reload (Pass 2 index-match in useCanvasSetup), leaving fills orphaned.
    // This secondary source lets the loader re-apply the color reliably.
    if (c._faceBgColors) design.faceBgColors = { ...c._faceBgColors };
    return design;
  }, []);

  // Single endpoint for the unified wraparound design
  const designEndpoint = 'wraparound-design';

  /** Write latest canvas state to localStorage (cheap & fast). */
  const saveLocally = useCallback(() => {
    const design = getDesignSnapshot();
    if (!design || !draftIdRef.current || !versionIdRef.current) return;
    setLocalDesign(draftIdRef.current, versionIdRef.current, design, 'wraparound');
  }, [getDesignSnapshot]);

  /**
   * Capture the front face of the canvas as a JPEG and upload it as the cover
   * preview image. Runs at most once per session (guarded by didUploadThumbRef).
   */
  const uploadThumbnail = useCallback(async () => {
    const c = canvasRef.current;
    const vid = versionIdRef.current;
    const did = draftIdRef.current;
    if (!c || !vid || !did) return;
    if (didUploadThumbRef.current) return;
    if (c.getObjects().length === 0) return;

    // Do not replace saved artwork with a render of a front face that has
    // nothing on it.
    //
    // This fires on load, before the author has touched anything, so whatever
    // it uploads becomes the cover everyone sees — and it does not care whether
    // the canvas came up looking like the cover that was saved.
    //
    // Seen on a real cover: an image whose "Appears On" was set to the spine
    // showed almost nothing on the front face, and this uploaded that near-empty
    // render over the finished artwork. Opening the cover was enough to destroy
    // it: no save, no click, no error. The render being wrong is one bug; the
    // render being allowed to overwrite good artwork is a separate one, and this
    // is the guard for the second.
    //
    // Measured from the PIXELS that would be uploaded, not from where the
    // objects are. An earlier version of this guard asked whether any object
    // overlapped the front face, and that let the damage straight through: an
    // object can span the front face and still draw nothing there — one scoped
    // to "Appears On: spine" does exactly that. The render is the only thing
    // that knows what the cover will look like.

    const dims = faceDimsRef.current || {};
    const faceW = dims.faceW || 360;
    const faceH = dims.faceH || 540;
    const spineW = dims.spineW || 100;
    const frontLeft = faceW + spineW;

    try {
      // Reset viewport to identity so toDataURL coords are in absolute canvas space
      const savedVPT = c.viewportTransform ? [...c.viewportTransform] : [1, 0, 0, 1, 0, 0];
      c.setViewportTransform([1, 0, 0, 1, 0, 0]);
      c.renderAll();

      const dataUrl = c.toDataURL({
        format: 'jpeg',
        quality: 0.85,
        left: frontLeft,
        top: 0,
        width: faceW,
        height: faceH,
        multiplier: 1,
      });

      // Restore the view the user was in
      c.setViewportTransform(savedVPT);
      c.renderAll();

      if (!dataUrl || dataUrl === 'data:,') return;

      // Refuse to publish a blank front face over stored artwork.
      //
      // Sampled from the region actually exported. A cover that renders as
      // near-empty is a symptom of something gone wrong upstream — a design
      // that failed to rehydrate, artwork scoped to another face — and in every
      // one of those cases the right move is to leave the saved cover alone and
      // let someone look, rather than overwrite good art with white.
      try {
        const ctx2 = c.getContext();
        const sample = ctx2.getImageData(frontLeft, 0, faceW, faceH).data;
        let inked = 0;
        for (let i = 0; i < sample.length; i += 4 * 53) {
          if (sample[i + 3] > 10
              && !(sample[i] > 245 && sample[i + 1] > 245 && sample[i + 2] > 245)) inked += 1;
        }
        const sampled = Math.ceil(sample.length / (4 * 53));
        if (inked / sampled < 0.02) {
          console.warn(
            `[useCanvasAutosave] front face rendered ${inked}/${sampled} inked samples — `
            + 'refusing to overwrite the stored cover image',
          );
          c.setViewportTransform(savedVPT);
          c.renderAll();
          return;
        }
      } catch (_e) {
        // A tainted canvas cannot be sampled. Uploading blind is what this
        // guard exists to prevent, so skip rather than risk the artwork.
        console.warn('[useCanvasAutosave] could not sample the front face — skipping the upload');
        return;
      }

      const accessToken = localStorage.getItem('rv_access_token');
      await fetch(
        `${API_BASE}/api/drafts/${did}/cover/versions/${vid}/image`,
        {
          method: 'PUT',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({ image: dataUrl }),
        },
      );
      // Mark done regardless of response — avoid hammering the endpoint
      didUploadThumbRef.current = true;
      hasCoverImageRef.current = true;
    } catch (_e) {
      // Non-critical — silently skip
    }
  }, []);

  /** PUT the design to the server. Returns true on success. */
  const saveToServer = useCallback(async (silent = true) => {
    const c = canvasRef.current;
    const vid = versionIdRef.current;
    const did = draftIdRef.current;
    if (!c || !vid || !did) return false;
    if (savingRef.current) return false;

    savingRef.current = true;
    setSaveStatus('saving');

    try {
      const accessToken = localStorage.getItem('rv_access_token');
      const design = getDesignSnapshot();

      const res = await fetch(
        `${API_BASE}/api/drafts/${did}/cover/versions/${vid}/${designEndpoint}`,
        {
          method: 'PUT',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({ design }),
        },
      );

      if (res.ok) {
        dirtyRef.current = false;
        silentFailNotedRef.current = false;
        setSaveStatus('saved');
        clearLocalDesign(did, vid, 'wraparound'); // server has latest
        if (!silent) toast.success('Progress saved!');

        // First-time thumbnail: capture front face for covers that were only autosaved
        if (!hasCoverImageRef.current && !didUploadThumbRef.current) {
          uploadThumbnail();
        }

        return true;
      }

      // Show what the server actually said. The body is JSON ({ error, code }),
      // and dumping it raw is how a real 413 "this design is too large" read as
      // the same generic failure as everything else.
      let detail = '';
      try {
        const body = await res.json();
        detail = body?.error || '';
      } catch (_e) {
        detail = '';
      }
      if (!detail) detail = `The server returned ${res.status}.`;

      setSaveStatus('unsaved');
      // A silent autosave that keeps failing is how a session's work gets lost
      // with nothing on screen. Say it once per failure streak, not every 60s.
      if (!silent || !silentFailNotedRef.current) {
        toast.error('Failed to save progress. ' + detail);
        if (silent) silentFailNotedRef.current = true;
      }
      return false;
    } catch (err) {
      console.error('[useCanvasAutosave] Server save failed:', err);
      setSaveStatus('unsaved');
      if (!silent || !silentFailNotedRef.current) {
        toast.error('Failed to save progress. Could not reach the server.');
        if (silent) silentFailNotedRef.current = true;
      }
      return false;
    } finally {
      savingRef.current = false;
    }
  }, [getDesignSnapshot, toast, uploadThumbnail]);

  // -- Public API: triggerChange ----------------------------------------------
  /** Call this whenever the canvas changes (object:modified/added/removed). */
  const triggerChange = useCallback(() => {
    dirtyRef.current = true;
    setSaveStatus('unsaved');

    // Debounced local save (resets on each call)
    if (localTimerRef.current) clearTimeout(localTimerRef.current);
    localTimerRef.current = setTimeout(() => saveLocally(), LOCAL_DEBOUNCE_MS);
  }, [saveLocally]);

  // -- Public API: forceSave --------------------------------------------------
  /** Immediately flush to server (used by manual save & export). */
  const forceSave = useCallback(async (silent = false) => {
    // Also write locally before the network call (instant safety)
    saveLocally();
    await saveToServer(silent);
  }, [saveLocally, saveToServer]);

  // -- Wire Fabric.js events --------------------------------------------------
  // Only activate AFTER hydration completes to avoid false-dirty from load events
  useEffect(() => {
    if (!canvas || readOnly || isHydrating) return;

    const handler = () => triggerChange();
    canvas.on('object:modified', handler);
    canvas.on('object:added', handler);
    canvas.on('object:removed', handler);

    return () => {
      canvas.off('object:modified', handler);
      canvas.off('object:added', handler);
      canvas.off('object:removed', handler);
    };
  }, [canvas, readOnly, isHydrating, triggerChange]);

  // -- Periodic server sync (60 s) -------------------------------------------
  useEffect(() => {
    if (!canvas || readOnly || !versionId) return;

    serverTimerRef.current = setInterval(() => {
      if (dirtyRef.current) {
        saveToServer(true); // silent
      }
    }, SERVER_SYNC_MS);

    return () => {
      if (serverTimerRef.current) clearInterval(serverTimerRef.current);
    };
  }, [canvas, readOnly, versionId, saveToServer]);

  // -- Tab close / navigate away (best-effort) -------------------------------
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!dirtyRef.current || !canvasRef.current || !versionIdRef.current) return;

      // 1. Ensure local snapshot is fresh
      saveLocally();

      // 2. Best-effort server flush via sendBeacon
      const design = getDesignSnapshot();
      if (design) {
        const accessToken = localStorage.getItem('rv_access_token');
        const blob = new Blob([JSON.stringify({ design })], { type: 'application/json' });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(
            API_BASE + '/api/drafts/' + draftIdRef.current + '/cover/versions/' + versionIdRef.current + '/' + designEndpoint + '?token=' + accessToken,
            blob,
          );
        }
      }

      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [saveLocally, getDesignSnapshot]);

  // -- Auto-capture thumbnail once per session on editor load ----------------
  // Always fires (regardless of hasCoverImage) so stale/wrong thumbnails get
  // replaced. didUploadThumbRef ensures it only runs once per session.
  useEffect(() => {
    if (isHydrating || readOnly || !canvas || !versionId) return;
    if (didUploadThumbRef.current) return;
    const t = setTimeout(() => uploadThumbnail(), 500);
    return () => clearTimeout(t);
  }, [isHydrating, readOnly, canvas, versionId, uploadThumbnail]);

  // -- Cleanup debounce timer on unmount --------------------------------------
  useEffect(() => {
    return () => {
      if (localTimerRef.current) clearTimeout(localTimerRef.current);
    };
  }, []);

  return { saveStatus, triggerChange, forceSave };
}
